import { useTranslation } from 'react-i18next';
import { VehicleTimelineDto, VehicleViewModel } from 'api/vehicles/models/VehicleViewModel';
import { VerticalTimeline } from 'react-vertical-timeline-component';
import { VerticalTimelineElement } from 'react-vertical-timeline-component';
import styles from './ReplacementTimeline.module.scss';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import ReactTooltip from 'react-tooltip';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { DATE_FORMAT_DEFAUT, DATE_TIME_FORMAT_DEFAUT } from 'Config';
import { FaPen } from 'react-icons/fa';
import { useState } from 'react';
import VehicleReplacementModal from '../../vehicleReplacementModal/VehicleReplacementModal';

type Props = {
    item: VehicleViewModel;
    onVehicleUpdated?: () => void | undefined;
    loadHistory?: (id:string) => void;
} & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const ReplacementTimeline: React.FC<Props> = ({
    item,
    onVehicleUpdated,
    loadHistory,
    ...props
}: Props) => {
    const { t } = useTranslation();
    const [dialogReplacementVehicleIsOpen, setDialogReplacementVehicleIsOpen] = useState<boolean>(false);
    const [replacementToEdit, setReplacementToEdit] = useState<VehicleTimelineDto | undefined>();

    const navigateToVehicle = (id: string | undefined) => {
        if (id) {
            window.open(`/vehicles/details/${id}/informations/list`, '_blank');
        }
    }

    const onEditReplacement = (replacement: VehicleTimelineDto) => {
        setDialogReplacementVehicleIsOpen(true);
        setReplacementToEdit(replacement);
    }

    const openVehicleHistory = (id: string | undefined) => {
        if (id && loadHistory) {
           loadHistory(id);
        }
    }

    // #66345: o autor pode já não resolver (utilizador eliminado). A entrada continua a ser mostrada
    // — antes o INNER JOIN no backend fazia-a desaparecer do histórico por completo.
    const renderUserName = (userName?: string) => userName ? <>{userName}</> : <i>{t('vehicle.deleted_user') as string}</i>;

    const renderReplacementHistoryItem = (history: VehicleTimelineDto, index: number) => {
        return (
            <VerticalTimelineElement
                key={`itemTimeline_${index}`}
                dateClassName={styles.dateClassName}>

                <div key={history.id} className={styles.item}>
                    {history.type == 'edit' ?
                        <div className={styles.date}>
                            <span data-tip={t('vehicle.update_date')} data-for={`updateDate_${index}`}><DateFormat value={history.historyCreatedDate ?? ''} format={DATE_TIME_FORMAT_DEFAUT} /></span>
                        </div>
                        :
                        <div className={styles.date}>
                            <span data-tip={t('vehicle.date_start_replacement')} data-for={`replacementDateStart_${index}`}><DateFormat value={history.replacementStartDate ?? ''} format={DATE_FORMAT_DEFAUT} /></span>
                            {history.replacementEndDate && <span data-tip={t('vehicle.date_end_replacement')} data-for={`replacementDateEnd_${index}`}> - <DateFormat value={history.replacementEndDate ?? ''} format={DATE_FORMAT_DEFAUT} /> </span>}
                        </div>}
                    {history.type == 'edit' ?
                        <div>
                            <span style={{ cursor: 'pointer' }} onClick={() => openVehicleHistory(history.id)}>
                                <div className={styles.title} data-tip={t('vehicle.open_edition')} data-for={`openEdition_${index}`}>
                                    <span>{t('vehicle.edited')}</span>
                                </div>
                            </span>
                        </div>
                        :
                        <div className={styles.infoContainer}>
                            <a style={{ cursor: 'pointer' }} onClick={() => navigateToVehicle(item?.id == history.replacementVehicleId ? history.vehicleId : history.replacementVehicleId)} className={styles.link}>
                                <div className={styles.title}>
                                    <span className={styles.subtitle}>{item?.id != history.vehicleId && item?.id != history.replacementVehicleId && `${history.vehicleBrandName ?? ''} ${history.vehicleModelName ?? ''} (${history.vehicleRegistrationNumber}) `}</span>
                                    <span>{item?.id == history.replacementVehicleId ? `${t('vehicle.replace_the')} ` : `${t('vehicle.replaced_by')} `}</span>
                                    <span className={styles.subtitle}>{item?.id == history.replacementVehicleId ? `${history.vehicleBrandName ?? ''} ${history.vehicleModelName ?? ''} (${history.vehicleRegistrationNumber})` : `${history.replacementVehicleBrandName ?? ''} ${history.replacementVehicleModelName ?? ''} (${history.replacementVehicleRegistrationNumber})`}</span>
                                </div>
                            </a>
                            {item?.id !== history.replacementVehicleId && <span className={styles.editIcon}
                                onClick={() => onEditReplacement(history)}
                                data-tip={t('replacement_vehicles.edit')}
                                data-for={`edit_${index}`}>
                                <FaPen className={styles.editIconSize} />
                            </span>}
                        </div>}
                    {!!history.reason && <div className={styles.reason} data-tip={t('vehicle.reason_replacement')} data-for={`reason_${index}`} >{history.reason}</div>}
                    {history.updateDate ? <div className={styles.user} data-tip={t('vehicle.update_date') + ': ' + new Date(history.updateDate).toLocaleDateString()} data-for={`lastUpdate_${index}`}>
                        <AvatarWithText src={history.userPhotoUrl} size="x2">
                            <span style={{ wordBreak: 'break-word', maxWidth: '90%' }}>
                                {renderUserName(history.userName)}
                            </span>
                        </AvatarWithText>
                    </div>
                        : <div className={styles.user} data-tip={t('vehicle.create_date') + new Date(history.createDate).toLocaleDateString()} data-for={`lastUpdate_${index}`}>
                            <AvatarWithText src={history.userPhotoUrl} size="x2">
                                <span style={{ wordBreak: 'break-word', maxWidth: '90%' }}>
                                    {renderUserName(history.userName)}
                                </span>
                            </AvatarWithText>
                        </div>}
                    <ReactTooltip id={`replacementDateStart_${index}`} />
                    <ReactTooltip id={`replacementDateEnd_${index}`} />
                    <ReactTooltip id={`reason_${index}`} />
                    {history.type != 'edit' && <ReactTooltip id={`lastUpdate_${index}`} />}
                    <ReactTooltip id={`updateDate_${index}`} />
                    <ReactTooltip id={`openEdition_${index}`} />

                    <ReactTooltip id={`edit_${index}`} />
                </div>
            </VerticalTimelineElement>
        );
    }

    return (
        <div className={styles.timeline}>
            {item.timelineHistory && item.timelineHistory.length > 0 &&
                <VerticalTimeline
                    animate={false}
                    layout={'1-column'}
                    className={styles.verticalTimeline}
                >
                    {item?.timelineHistory.map((x: VehicleTimelineDto, idx: number) => (
                        renderReplacementHistoryItem(x, idx)
                    ))}
                </VerticalTimeline>
            }

            <VehicleReplacementModal
                isVisible={dialogReplacementVehicleIsOpen}
                item={replacementToEdit}
                isEdit={true}
                replacedVehicle={item?.replacedVehicle ? item?.id : undefined}
                currentVehicle={item}
                onSave={() => {
                    setDialogReplacementVehicleIsOpen(false);
                    onVehicleUpdated && onVehicleUpdated();
                }}
                onCancel={() => setDialogReplacementVehicleIsOpen(false)}
            />
        </div>
    );
}

export default ReplacementTimeline;
